/**
 * Local DLT perspective transform (same API surface as `perspective-transform`).
 * Avoids that package's broken UMD/numeric shim under Vite ESM, which crashed
 * the whole reader on import (`Cannot set properties of undefined (setting 'numeric')`).
 */

export type CornerCoords = [
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
];

export type PerspectiveTransform = {
  transform(x: number, y: number): [number, number];
  transformInverse(x: number, y: number): [number, number];
};

function applyCoeffs(coeffs: number[], x: number, y: number): [number, number] {
  const w = coeffs[6]! * x + coeffs[7]! * y + 1;
  return [
    (coeffs[0]! * x + coeffs[1]! * y + coeffs[2]!) / w,
    (coeffs[3]! * x + coeffs[4]! * y + coeffs[5]!) / w,
  ];
}

/** Solve A x = b (n×n) with partial pivoting. Returns null if singular. */
function solveLinearSystem(A: number[][], b: number[]): number[] | null {
  const n = b.length;
  const m = A.map((row, i) => [...row, b[i]!]);

  for (let col = 0; col < n; col++) {
    let pivot = col;
    for (let row = col + 1; row < n; row++) {
      if (Math.abs(m[row]![col]!) > Math.abs(m[pivot]![col]!)) pivot = row;
    }
    if (Math.abs(m[pivot]![col]!) < 1e-12) return null;
    if (pivot !== col) {
      const tmp = m[col]!;
      m[col] = m[pivot]!;
      m[pivot] = tmp;
    }
    const piv = m[col]![col]!;
    for (let j = col; j <= n; j++) m[col]![j]! /= piv;
    for (let row = 0; row < n; row++) {
      if (row === col) continue;
      const factor = m[row]![col]!;
      if (factor === 0) continue;
      for (let j = col; j <= n; j++) m[row]![j]! -= factor * m[col]![j]!;
    }
  }

  return m.map((row) => row[n]!);
}

/**
 * Coefficients for mapping srcPts → dstPts (h8 fixed to 1).
 * Flat point lists: [x0,y0, x1,y1, x2,y2, x3,y3].
 */
function getNormalizationCoefficients(
  srcPts: number[],
  dstPts: number[],
): number[] {
  const A: number[][] = [];
  const b: number[] = [];

  for (let i = 0; i < 4; i++) {
    const sx = srcPts[i * 2]!;
    const sy = srcPts[i * 2 + 1]!;
    const dx = dstPts[i * 2]!;
    const dy = dstPts[i * 2 + 1]!;
    A.push([sx, sy, 1, 0, 0, 0, -dx * sx, -dx * sy]);
    b.push(dx);
    A.push([0, 0, 0, sx, sy, 1, -dy * sx, -dy * sy]);
    b.push(dy);
  }

  const solved = solveLinearSystem(A, b);
  if (!solved) {
    // Identity-like fallback (mirrors perspective-transform try/catch behavior)
    return [1, 0, 0, 0, 1, 0, 0, 0];
  }
  return solved;
}

export function createPerspectiveTransform(
  srcPts: number[] | CornerCoords,
  dstPts: number[] | CornerCoords,
): PerspectiveTransform {
  const coeffs = getNormalizationCoefficients(srcPts, dstPts);
  const coeffsInv = getNormalizationCoefficients(dstPts, srcPts);
  return {
    transform(x, y) {
      return applyCoeffs(coeffs, x, y);
    },
    transformInverse(x, y) {
      return applyCoeffs(coeffsInv, x, y);
    },
  };
}
